import { Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgDateFilter from '../../components/common/customAgGrid/CustomAgDateFilter';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import EditManagementPeriodModal from '../../components/managementperiods/EditManagementPeriodModal';
import PageHeader from '../../components/PageHeader';
import {
  useGetManagementPeriodsListQuery,
  useUpdateManagementPeriodMutation,
} from '../../store/apiSlices/managementperiods/managementPeriodsApis';
import { useSelector } from 'react-redux';
import { canEditManagementPeriods } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { dateRangeFilterPredicate } from '../../utils/agGridPredicates';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface ManagementPeriod {
  id: string | number;
  vesselId?: number;
  vesselName: string;
  manager: string;
  dateRange: string;
  status: 'Active' | 'Inactive';
}

interface ActionButton {
  label: string;
  variant: 'blue' | 'red' | 'text' | 'outlinedBlue' | 'gray';
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
const ManagementPeriodsContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canEdit = canEditManagementPeriods(userInfo?.authorityName);
  const [showEditManagementPeriodModal, setShowEditManagementPeriodModal] =
    useState<boolean>(false);
  const [editingManagementPeriod, setEditingManagementPeriod] = useState<ManagementPeriod | null>(
    null,
  );
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  // Mock Api call
  const {
    data: managementPeriodsList,
    isLoading,
    refetch,
  } = useGetManagementPeriodsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // API mutation for updating management period
  const [updateManagementPeriod, { isLoading: isUpdating }] = useUpdateManagementPeriodMutation();

  // Page header action buttons
  const actionBtns: ActionButton[] = [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('managementPeriods.title'), isActive: true, onClick: () => {} },
  ];

  // Handle edit button click
  const handleEditClick = useCallback((managementPeriod: ManagementPeriod) => {
    setEditingManagementPeriod(managementPeriod);
    setShowEditManagementPeriodModal(true);
  }, []);

  // Column definitions
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'vesselName',
        headerName: t('table.columns.vesselName'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 200,
        minWidth: 150,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'manager',
        headerName: t('table.columns.manager'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 200,
        minWidth: 150,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'dateRange',
        headerName: t('table.columns.date'),
        sortable: true,
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        width: 200,
        minWidth: 150,
        floatingFilterComponent: CustomAgDateFilter,
        filterParams: {
          maxNumConditions: 1, // hide AND/OR
          suppressAndCondition: true,
          filterOptions: [
            {
              displayKey: 'inRange',
              displayName: t('common.between'),
              numberOfInputs: 2,
              predicate: dateRangeFilterPredicate,
            },
          ],
        },
      },
      {
        field: 'status',
        headerName: t('table.columns.status'),
        sortable: true,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        width: 120,
        minWidth: 100,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips width="100px" label={params.value} />,
      },
      {
        field: 'action',
        headerName: '',
        sortable: false,
        width: 50,
        maxWidth: 50,
        flex: 0,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
            ]}
            rowData={params.data}
          />
        ),
      },
    ],
    [handleEditClick, t],
  );

  useEffect(() => {
    setTableColumns(canEdit ? columns : columns?.filter((col: any) => col.field !== 'action'));
  }, [canEdit, columns]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: { status: 'Active' | 'Inactive' }) => {
      if (editingManagementPeriod) {
        try {
          // Convert status to boolean for API
          const active = formData.status === 'Active';

          const res: any = await updateManagementPeriod({
            id: Number(editingManagementPeriod.id),
            vesselId: editingManagementPeriod.vesselId || 0,
            active,
          }).unwrap();
          showSuccessToast(res?.message);

          // Refetch data after successful update
          refetch();
          setShowEditManagementPeriodModal(false);
          setEditingManagementPeriod(null);
        } catch (error) {
          logger.error('Failed to update management period', error as Error);
        }
      }
    },
    [editingManagementPeriod, updateManagementPeriod, refetch],
  );

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setShowEditManagementPeriodModal(false);
    setEditingManagementPeriod(null);
  }, []);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={managementPeriodsList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="management-periods-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showEditManagementPeriodModal && managementPeriodsList && (
        <EditManagementPeriodModal
          data={{ managementPeriods: managementPeriodsList as any }}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
          managementPeriod={editingManagementPeriod}
          isEditMode={!!editingManagementPeriod}
          isLoading={isUpdating}
        />
      )}
    </Stack>
  );
};

export default ManagementPeriodsContainer;
